import { useMemo } from 'react'

import GlobalOutlinedIcon from '@ant-design/icons/GlobalOutlined'
import HomeOutlinedIcon from '@ant-design/icons/HomeOutlined'
import IdcardOutlinedIcon from '@ant-design/icons/IdcardOutlined'
import MedicineBoxOutlinedIcon from '@ant-design/icons/MedicineBoxOutlined'
import UserAddOutlinedIcon from '@ant-design/icons/UserAddOutlined'

import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@components/_mui/Typography'

import usePatient from '@shared/hooks/src/usePatient'

import useInsuranceSummary from '@hooks/useInsuranceSummary'
import { CBOCardPopper } from '@components/CBOCard'
import { InsuranceCardPopper } from '@components/InsuranceCard'

import { PropertyIcon } from '../Property'
import RequiredFields from '../RequiredFields'
import { FAQIcon, PatientHeader, styles } from './PatientCard.utils'

export default function PatientCard({ patientId, title, children }) {
  const { data: patient, isPending } = usePatient(patientId)

  const content = useMemo(() => {
    if (typeof children === 'function') return children({ patient })
    return children
  }, [children, patient])

  if (isPending || !patient) return <Loading />

  return (
    <Paper sx={styles.container}>
      <Stack spacing={3}>
        <PatientHeader user={patient} />
        <Stack spacing={1}>
          <Typography variant="h4">{title}</Typography>
          <Grid container spacing={1}>
            <Grid size={6}>
              <PropertyIcon icon={HomeOutlinedIcon} label="Location">
                {[patient.homeAddress?.city, patient.homeAddress?.state].filter(Boolean).join(', ')}
              </PropertyIcon>
            </Grid>
            <Grid size={6}>
              <PropertyIcon icon={GlobalOutlinedIcon} label="Language">
                {patient.language || '-'}
              </PropertyIcon>
            </Grid>
            <Grid size={6}>
              <PropertyIcon icon={IdcardOutlinedIcon} label="Insurance">
                <Insurance patientId={patientId} />
              </PropertyIcon>
            </Grid>
            <Grid size={6}>
              <PropertyIcon icon={MedicineBoxOutlinedIcon} label="CBO">
                <CBOCardPopper cboId={patient.cbo?.id}>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Typography>{patient.cbo?.name || '-'}</Typography>
                    {patient.cbo && <FAQIcon />}
                  </Stack>
                </CBOCardPopper>
              </PropertyIcon>
            </Grid>
            <Grid size={6}>
              <PropertyIcon icon={MedicineBoxOutlinedIcon} label="Pharmacy">
                {patient.pharmacy?.name || '-'}
              </PropertyIcon>
            </Grid>
            <Grid size={6}>
              <PropertyIcon icon={UserAddOutlinedIcon} label="Gender">
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <Typography>{patient.gender || '-'}</Typography>
                  {patient.bornAs && patient.bornAs !== patient.gender && (
                    <Chip label={`Born as ${patient.bornAs}`} size="small" variant="outlined" />
                  )}
                </Stack>
              </PropertyIcon>
            </Grid>
          </Grid>
        </Stack>
        {content}
      </Stack>
    </Paper>
  )
}

function Loading() {
  return (
    <Paper sx={styles.container}>
      <Stack spacing={3}>
        <PatientHeader.Loading />
        <Stack spacing={1}>
          <Typography variant="h4">
            <Skeleton width={160} />
          </Typography>
          <Grid container spacing={1}>
            <Grid size={6}>
              <PropertyIcon.Loading width={120} />
            </Grid>
            <Grid size={6}>
              <PropertyIcon.Loading width={100} />
            </Grid>
            <Grid size={6}>
              <PropertyIcon.Loading width={140} />
            </Grid>
            <Grid size={6}>
              <PropertyIcon.Loading width={130} />
            </Grid>
            <Grid size={6}>
              <PropertyIcon.Loading width={120} />
            </Grid>
            <Grid size={6}>
              <PropertyIcon.Loading width={80} />
            </Grid>
          </Grid>
        </Stack>
        <RequiredFields.Loading />
      </Stack>
    </Paper>
  )
}

PatientCard.Loading = Loading

function Insurance({ patientId }) {
  const { data, isPending } = useInsuranceSummary(patientId)

  if (isPending) {
    return (
      <Typography>
        <Skeleton width={140} />
      </Typography>
    )
  }

  if (!data?.lastActiveInsurance) {
    return <Typography>-</Typography>
  }

  return (
    <InsuranceCardPopper patientId={patientId} insuranceId={data.lastActiveInsurance.id}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <Typography>{data.lastActiveInsurance.company}</Typography>
        <FAQIcon />
      </Stack>
    </InsuranceCardPopper>
  )
}
